import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from 'react-router';
import { Button, MenuItem, TextField, Popover, IconButton } from "@mui/material";
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import FilterListIcon from "@mui/icons-material/FilterList";
import { DatePicker, DesktopDateTimePicker, renderMultiSectionDigitalClockTimeView, TimePicker } from "@mui/x-date-pickers";
import moment from "moment";

type FilterButtonProps = {
  column: string;
  type: string;
};

type InstalledFilter = {
  operator?: string
  value?: string
}

export const FilterButton = ({ column, type }: FilterButtonProps) => {
  const { t }                          = useTranslation();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isOpen = Boolean(anchorEl);
  const id = isOpen ? column + "-filter-popover" : undefined;

  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const navigate = useNavigate();

  const activeFilter = hasURLParam(column)

  const OPERATORS_BY_TYPE = {
    text: [
      { value: "eq", label: t("filterOptions.equals") },
      { value: "ct", label: t("filterOptions.contains") },
      { value: "be", label: t("filterOptions.beginsWith") },
      { value: "en", label: t("filterOptions.endsWith") },
    ],
    num: [
      { value: "eq", label: "=" },
      { value: "gt", label: ">" },
      { value: "lt", label: "<" },
    ],
    temporal: [
      { value: "eq", label: "=" },
      { value: "gt", label: ">" },
      { value: "lt", label: "<" },
    ],
    enum: [
      { value: "eq", label: "=" },
    ],
    none: []
  };

  const typeMap: Record<string, "text" | "num" | "enum" | "temporal"> = {
    text: "text",
    integer: "num",
    decimal: "num",
    date: "temporal",
    datetime: "temporal",
    time: "temporal",
    boolean: "enum",
  };

  const isTemporal: boolean = ["date", "datetime", "time"].includes(type);
    

  //handling user actions
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleKeyDown = (event : React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      applyFilter();
    }
  };

  // URL methods
  function updateURLParam(key : string, value : string) {
    params.set(key, encodeURIComponent(value))
    navigate({
      pathname: location.pathname,
      search: params.toString(),
    });
  }
  function removeURLParam(key : string) {
    params.delete(key);
    navigate({
      pathname: location.pathname,
      search: params.toString(),
    });
  }

  function getURLParam(key : string) : string | null{
    return decodeURIComponent(params.get(key) ?? "");
  }

  function hasURLParam(key : string) : boolean {
    return params.has(key)
  }
  
  // Filter apply/removing and current status
  const dateToString = (kind: string, dateVar : Date | null) : string => {
    switch(kind) {
      case "date" :     return moment(dateVar).format("YYYY-MM-DD");
      case "datetime" : return moment(dateVar).format("YYYY-MM-DDTHH:mm:ssZ");
      case "time" :     return moment(dateVar).format("HH:mm:ss");
      default:          return ""
    }
  }

  const stringToDate = (kind: string, dateVar: string | undefined | null): Date => {
    switch (kind) {
      case "date":      return moment(dateVar, "YYYY-MM-DD", true).toDate();
      case "datetime":  return moment(dateVar, "YYYY-MM-DDTHH:mm:ssZ", true).toDate();
      case "time":      return moment(dateVar, "HH:mm:ss", true).toDate();
      default:          return new Date(Date.now());
    }
  };

  const defaultOperator = {num: "eq", text: "ct", enum: "eq", temporal: "gt"}[typeMap[type]] ?? "";
  const defaultValue : string = {num: "0", text: "", enum: "", temporal: dateToString(type, new Date(Date.now()))}[typeMap[type]] ?? "";

  const [installedFilter, setInstalledFilter] = useState<InstalledFilter>({
    operator: hasURLParam(column) ?  getURLParam(column)?.slice(0, 2) : defaultOperator,
    value: hasURLParam(column) ? getURLParam(column)?.slice(3) : defaultValue,
  });

  const applyFilter = () => {
    updateURLParam(column, `${installedFilter.operator}_${installedFilter.value}`)
    setAnchorEl(null);
  }

  const removeFilter = (event : React.MouseEvent<HTMLButtonElement>) => {
    setInstalledFilter({operator : defaultOperator, value : defaultValue})
    removeURLParam(column);
    setAnchorEl(null);
  }

  useEffect(() => {
    if (hasURLParam(column)) {
      const param = getURLParam(column)!;

      setInstalledFilter({
        operator: param.slice(0, 2),
        value: param.slice(3),
      });
    }
  }, [location.search]);

  //return

  return (
      <>
        <IconButton id={"filterButton-" + column} onClick={handleClick}>
          {activeFilter ? (<FilterListIcon color={"action"}/>) : (<FilterListOffIcon color={"disabled"}/>)}
        </IconButton>
        <Popover 
          id={id}
          open={isOpen}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}>
          <div style={{ padding: 8, display: "flex", flexDirection: "row", gap: 12, minWidth: 200 }}>
            <TextField
              size="small"
              select
              label="operator"
              value={installedFilter.operator}
              style={{minWidth: 80}}
              onChange={(e) => setInstalledFilter(filter => ({operator : e.target.value, value: filter.value}))}
            >
              {(OPERATORS_BY_TYPE[typeMap[type] ?? "none"] ?? []).map((op) => (
                <MenuItem key={op.value} value={op.value}>
                  {op.label}
                </MenuItem>
              ))}
            </TextField>

            {!isTemporal && 
              <TextField
              size="small"
              label={t("filterOptions.comparisonValue")}
              value={installedFilter.value}
              onChange={(e) => setInstalledFilter(filter => ({operator : filter.operator, value: e.target.value}))}
              onKeyDown={handleKeyDown}
            />}

            {type === "date" &&
              <DatePicker
                displayWeekNumber={true}
                className="grs-InputDateField"
                desktopModeMediaQuery="@media (not (pointer: coarse))"
                format="dd-MM-yyyy"
                // views={["month", "day"]}
                value={stringToDate("date", installedFilter.value)}
                slotProps={{
                  textField: {
                    size: "small",
                  }
                }}
                onChange={(val, con) => setInstalledFilter(filter => ({operator : filter.operator, value: dateToString("date", val)}))}
              />
            }
            {type === "datetime" && 
              <DesktopDateTimePicker
                displayWeekNumber={true}
                //desktopModeMediaQuery="@media (not (pointer: coarse))"
                format="dd-MM-yyyy HH:mm:ss"
                ampm={false}
                ampmInClock={false}
                value={stringToDate("datetime", installedFilter.value)}
          
                views={["year", "month","day", "hours", "minutes", "seconds"]}
                viewRenderers={{
                  hours: renderMultiSectionDigitalClockTimeView,
                  minutes: renderMultiSectionDigitalClockTimeView,
                  seconds: renderMultiSectionDigitalClockTimeView,
                }}
                slotProps={{
                  textField: {
                    size: "small",
                  }
                }}
          
                onChange={(val, con) => setInstalledFilter(filter => ({operator : filter.operator, value: dateToString("datetime", val)}))}
              />
            }
            {type === "time"&& 
              <TimePicker
                format="HH:mm:ss"
                ampm={false}
                value={stringToDate("time", installedFilter.value)}
                views={["hours", "minutes", "seconds"]} 
                viewRenderers={{
                  hours: renderMultiSectionDigitalClockTimeView,
                  minutes: renderMultiSectionDigitalClockTimeView,
                  seconds:renderMultiSectionDigitalClockTimeView 
                }}

                slotProps={{
                  textField: {
                    size: "small",
                  }
                }}
                onChange={(val, con) => setInstalledFilter(filter => ({operator : filter.operator, value: dateToString("time", val)}))}
              />
            }

            <Button size="small" variant="contained" onClick={e => applyFilter()} color={"primary"} type={"submit"}>
              {t("filterOptions.apply")}
            </Button>
            <Button size="small" variant="contained" onClick={removeFilter} color={"error"}>
              {t("filterOptions.remove")}
            </Button>
          </div>
        </Popover>
      </>
    )
}
